import { createContext, useContext, useState, type ReactNode, type ReactElement } from "react";

export type Language = "en" | "es";

interface LanguageState {
  lang: Language;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageState | undefined>(undefined);
const LOCAL_STORAGE_KEY = "lang";

const getInitialLang = (): Language => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored === "en" || stored === "es") return stored;
  const browserLang = navigator.language.slice(0, 2);
  return browserLang === "es" ? "es" : "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [lang, setLang] = useState<Language>(getInitialLang);

  const toggleLanguage = (): void => {
    setLang((prev) => {
      const next: Language = prev === "en" ? "es" : "en";
      localStorage.setItem(LOCAL_STORAGE_KEY, next);
      return next;
    });
  };

  return (
    <LanguageContext.Provider value={{ lang, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageState => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
